import type { ReactNode } from "react";
import { Flex } from "styles/utility-components";

import Button from "components/ui/button/Button";
import UIcon from "lib/icons/untitled_ui/UIcon";

export type CursorPaginationProps = {
  /** Whether a next page exists (e.g. provider returned a forward cursor). */
  hasNextPage: boolean;
  /** Whether the caller can return to a previous page. */
  canGoPrevious: boolean;
  onNext: () => void;
  onPrevious: () => void;
  /** Disables both controls while a page is loading. */
  isLoading?: boolean;
  /** Optional context shown alongside the controls, e.g. "1,234 contacts". */
  rangeLabel?: ReactNode;
};

/**
 * Forward/back controls for cursor-paginated data. There is no jump-to-page:
 * cursors are opaque and "Previous" relies on the caller tracking a cursor
 * stack (see `useCursorPagination`).
 */
function CursorPagination({
  hasNextPage,
  canGoPrevious,
  onNext,
  onPrevious,
  isLoading = false,
  rangeLabel,
}: CursorPaginationProps) {
  return (
    <Flex justifyContent="center" alignItems="center" gap={12}>
      <Button
        buttonType="secondary"
        icon="chevronLeft"
        disabled={!canGoPrevious || isLoading}
        onClick={onPrevious}
        aria-label="Previous page"
      >
        Previous
      </Button>
      {rangeLabel}
      <Button
        buttonType="secondary"
        disabled={!hasNextPage || isLoading}
        onClick={onNext}
        aria-label="Next page"
      >
        Next
        <UIcon icon="chevronRight" size={16} color="sysPrimaryDefault" className="icon" />
      </Button>
    </Flex>
  );
}

export default CursorPagination;
